import React, { useEffect, useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { difference, equals, gt, includes, length, lt, pluck } from "ramda";
import { capitalize } from "util/stringFormat";
import { useHandleOpenViewerClick } from "../../../media-viewer/mediaViewerRedux";
import { notBlank } from "../../../util/ext";
import { useMediaTable } from "../../common/use-mediaTable";
import {
  useRouteMatch,
  useHistory,
  Link as RouterLink,
} from "react-router-dom";
import { useIsAdmin } from "../../../auth/authUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/pro-regular-svg-icons";

import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Tooltip from "@material-ui/core/Tooltip";

import ContentCard from "./ContentCard";
import ContentOriginCard from "../../common/ContentOriginCard";
import ContentParentsCard from "../../organize/content-groups/card/ContentParentsCard";
import CustomTablePagination from "../../common/CustomTablePagination";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import OwnersList from "../../modules/browse/OwnersList";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  icon: {
    color: theme.palette.primary.darker,
  },

  contentCell: {
    width: "33%",
    paddingRight: theme.spacing(3),
  },

  cell: {
    minWidth: 80,
  },

  contentTypeCell: {
    textTransform: "capitalize",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const initialCheckbox = {
  checked: false,
  indeterminate: false,
};

const ContentTable = props => {
  const { url } = useRouteMatch();

  const {
    contentList,
    page,
    onPageChange,
    total = 0,
    onSelectAllContent,
    onSelectChange,
    onRowClick,
    orderBy = "name",
    order = "asc",
    rowsPerPage = 10,
    onRowsPerPageChange,
    onSortChange,
    onScrollVisible,
    searchState,
    selected,
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const isAdmin = useIsAdmin();
  const {
    handleRequestSort,
    handleChangePage,
    handleUpdateRowsPerPage,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
      onRowsPerPageChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      rowsPerPage,
      editId: null,
    },
  );

  const { handleOpenViewerClick, isViewerLoading } = useHandleOpenViewerClick();

  const handleThumbnailClick = content => event => {
    event.stopPropagation();
    handleOpenViewerClick(content);
  };

  const handleTitleClick = content => event => {
    event.stopPropagation();
    history.push(`${url}/${content.id}`);
  };

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    // console.log("Setting scroll visible...");
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  const [checkbox, setCheckbox] = useState(initialCheckbox);
  useEffect(() => {
    const diff = difference(pluck("id", contentList), selected);

    setCheckbox({
      checked: equals(length(diff), 0),
      indeterminate:
        gt(length(diff), 0) && lt(length(diff), length(contentList)),
    });
  }, [total, selected, contentList]);

  return (
    <Paper elevation={0} className={classes.root}>
      {isViewerLoading && <PanelLoadingIndicator />}
      <TableContainer ref={tableContainerEl}>
        <Table stickyHeader aria-label="Content">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" align="center">
                <Checkbox
                  color="primary"
                  indeterminate={checkbox.indeterminate}
                  checked={checkbox.checked}
                  onChange={event => onSelectAllContent(checkbox, contentList)}
                  inputProps={{ "aria-label": "Select" }}
                />
              </TableCell>

              <TableCell
                key="content"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.contentCell}
              >
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={order}
                  onClick={() => handleRequestSort("name")}
                >
                  Title
                  {orderBy === "name" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="date-uploaded"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "auditAdded"}
                  direction={order}
                  onClick={() => handleRequestSort("auditAdded")}
                >
                  Uploaded
                  {orderBy === "auditAdded" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="content-type"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "contentType"}
                  direction={order}
                  onClick={() => handleRequestSort("contentType")}
                >
                  Content Type
                  {orderBy === "contentType" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="origin"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.cell}
              >
                Origin
              </TableCell>

              {isAdmin && (
                <TableCell
                  key="owners"
                  align="left"
                  classes={{ stickyHeader: classes.headerCell }}
                  className={classes.cell}
                >
                  Library/Organization
                </TableCell>
              )}

              <TableCell
                key="included-in"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.cell}
                style={{ paddingLeft: 20 }}
              >
                Included In
              </TableCell>

              {isAdmin && (
                <TableCell
                  key="manage-content-access"
                  align="center"
                  classes={{ stickyHeader: classes.headerCell }}
                  style={{ width: 50 }}
                >
                  Access Control
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {contentList.map(content => (
              <TableRow
                onClick={() => onRowClick(content)}
                hover
                tabIndex={-1}
                key={content.id}
                role="checkbox"
                aria-checked={includes(content.id, selected)}
                selected={includes(content.id, selected)}
              >
                <TableCell padding="checkbox" align="center">
                  <Checkbox
                    color="primary"
                    checked={includes(content.id, selected)}
                    disabled={content.status === "TAKEDOWN"}
                    inputProps={{
                      "aria-labelledby": "content-checkbox-" + content.id,
                    }}
                  />
                </TableCell>

                <TableCell
                  key={"title:" + content.id}
                  align="left"
                  className={classes.contentCell}
                >
                  <ContentCard
                    content={content}
                    handleThumbnailClick={handleThumbnailClick(content)}
                    handleTitleClick={handleTitleClick(content)}
                  />
                </TableCell>

                <TableCell key={"date-uploaded:" + content.id} align="left">
                  {new Date(content.auditAdded).toLocaleDateString()}
                </TableCell>

                <TableCell
                  key={"content-type:" + content.id}
                  align="left"
                  className={classes.contentTypeCell}
                >
                  {capitalize(content.type)}
                </TableCell>

                <TableCell key={"origin:" + content.id} align="left">
                  <ContentOriginCard content={content} />
                </TableCell>

                {isAdmin && (
                  <TableCell key={"owners:" + content.id}>
                    <OwnersList media={content} searchState={searchState} />
                  </TableCell>
                )}

                <TableCell key={"included-in: " + content.id}>
                  <ContentParentsCard content={content} />
                </TableCell>

                {isAdmin && (
                  <TableCell
                    key={"content access:" + content.id}
                    align="center"
                  >
                    <Tooltip title="Manage Access">
                      <IconButton
                        component={RouterLink}
                        to={{
                          pathname: `/creator/manage-access/content/${content.id}`,
                          state: {
                            label: "Back to All Content",
                            previousLocation: "/creator/content",
                          },
                        }}
                      >
                        <FontAwesomeIcon icon={faKey} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomTablePagination
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangeRowsPerPage={event =>
          handleUpdateRowsPerPage(+event.target.value)
        }
        onChangePage={(event, page) => handleChangePage(page)}
      />
    </Paper>
  );
};

export default ContentTable;
